import {cn} from '@tempmee/core'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'

export interface ScrimProps extends HTMLAttributes<HTMLDivElement> {
  /** Whether the scrim is visible. When `false`, nothing renders. */
  open: boolean
  /**
   * Render a transparent (click-catching) scrim instead of the dim fill — used
   * for click-away dismissal of menus/popovers. @default false
   */
  invisible?: boolean
  /** Optional centered content (e.g. a `CircularProgress` loading spinner). */
  children?: ReactNode
}

/**
 * Scrim (v3, MUI-aligned) — a full-surface dim overlay behind modal content,
 * or a transparent click-catcher.
 *
 * Maps to `@mui/material/Backdrop` (Figma labels it "Scrim"). Fills its nearest
 * positioned ancestor (`fixed inset-0`) with the `backdrop` token
 * (`#00091A80`), centers optional `children`, and renders nothing while closed.
 * Stacking defaults to `z-50` and is overridable via `className`.
 *
 * Figma: https://www.figma.com/design/11epclteWbmxaDMj2JUJW8/Design-System-Shift?node-id=309-3140
 *
 * @example
 * <Scrim open onClick={close} />
 * <Scrim open><CircularProgress color="inherit" /></Scrim>
 */
export const Scrim = forwardRef<HTMLDivElement, ScrimProps>(
  ({open, invisible = false, className, children, ...props}, ref) => {
    if (!open) return null

    return (
      <div
        ref={ref}
        role="presentation"
        aria-hidden="true"
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center',
          !invisible && 'bg-backdrop',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },
)
Scrim.displayName = 'Scrim'
